/**
 * MergeTicketModal - fold duplicate tickets into a surviving master.
 *
 * Two directions, one modal (plan
 * plans/2026-07-15-ticket-merge-and-thread-routing.md, D9 / step 13):
 *
 *   - "into":   the OPEN ticket becomes part of another ticket the
 *               agent picks (single-select). This is the common flow
 *               when the agent is looking AT the dupe.
 *   - "master": the open ticket survives; the agent multi-selects the
 *               dupes to fold into it. This is the flow for today's
 *               real incident (4 dupes -> 1 saga).
 *
 * Ticket lookup reuses the agent hybrid search endpoint via
 * useAgentTicketSearch with a 250 ms debounce (the useUserSearch
 * convention). Gemini archive rows (G-*) and the open ticket itself
 * render disabled - the backend enforces the same rules with a 409,
 * the UI just saves the round trip.
 *
 * After a merge, replies to ANY merged ticket route to the master
 * permanently (subject token, References, fuzzy, portal, API), so the
 * confirm row spells out exactly which ticket survives.
 */
import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";

import { Alert, Button, Modal, Spinner } from "@/components/ui";
import { StatusBadge } from "@/components/tickets/StatusBadge";
import { useAgentTicketSearch } from "@/hooks/useAgentTicketSearch";
import { useMergeTickets } from "@/hooks/useMergeTickets";
import type {
  SearchResultResponse,
  TicketResponse,
  TicketStatus,
} from "@/types/api";

export type MergeDirection = "into" | "master";

export interface MergeTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The ticket whose detail page the agent is on. */
  ticket: TicketResponse;
  /** Called after a successful merge with the MASTER's public code,
   * the direction that ran, and the folded tickets' public codes; the
   * page navigates / announces off it. */
  onMerged: (
    masterPublicCode: string,
    direction: MergeDirection,
    sourcePublicCodes: string[],
  ) => void;
}

// Mirrors ticket_merge_service.MAX_SOURCES_PER_MERGE. Enforced client-
// side so the agent hits a friendly inline message instead of the
// FastAPI 422 (whose array-shaped detail is not operator prose).
const MAX_SOURCES_PER_MERGE = 20;

function errorDetail(err: unknown): string {
  if (isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string" && detail) return detail;
    // Pydantic validation errors arrive as a list of {msg, loc, type}.
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) =>
          typeof (d as { msg?: unknown })?.msg === "string"
            ? (d as { msg: string }).msg
            : null,
        )
        .filter((m): m is string => !!m);
      if (msgs.length > 0) return msgs.join("; ");
    }
  }
  // Timeouts / dropped responses can leave the request COMMITTED
  // server-side (the merge is all-or-nothing but the response can
  // still be lost), so do not promise nothing changed.
  return "Merge may not have completed - refresh and check the ticket before retrying.";
}

function isGeminiCode(publicCode: string): boolean {
  return /^g-/i.test(publicCode);
}

export function MergeTicketModal({
  isOpen,
  onClose,
  ticket,
  onMerged,
}: MergeTicketModalProps) {
  const [direction, setDirection] = useState<MergeDirection>("into");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState<SearchResultResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const mergeTickets = useMergeTickets();

  // Reset per open so a re-opened modal never carries a stale pick.
  useEffect(() => {
    if (isOpen) {
      setDirection("into");
      setQuery("");
      setDebouncedQuery("");
      setSelected([]);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  // Blank query while closed disables the underlying useInfiniteQuery,
  // so a keystroke followed by a quick close cannot fire a search
  // against a dismissed dialog.
  const search = useAgentTicketSearch(isOpen ? debouncedQuery : "", {}, 10);
  const results = useMemo(
    () => (search.data?.pages ?? []).flatMap((p) => p.data),
    [search.data],
  );

  const requesterDiffers = selected.some(
    (s) =>
      s.requester_email.toLowerCase() !==
      ticket.requester_email.toLowerCase(),
  );

  function toggle(row: SearchResultResponse) {
    const already = selected.some((s) => s.ticket_id === row.ticket_id);
    if (already) {
      setError(null);
      setSelected(selected.filter((s) => s.ticket_id !== row.ticket_id));
      return;
    }
    // Single-select when this ticket merges INTO the pick.
    if (direction === "into") {
      setError(null);
      setSelected([row]);
      return;
    }
    if (selected.length >= MAX_SOURCES_PER_MERGE) {
      setError(
        `At most ${MAX_SOURCES_PER_MERGE} tickets can be merged in one request.`,
      );
      return;
    }
    setError(null);
    setSelected([...selected, row]);
  }

  function switchDirection(next: MergeDirection) {
    if (next === direction) return;
    setDirection(next);
    setSelected([]);
    setError(null);
  }

  // Every dismiss affordance (Escape, overlay click, X, Cancel) routes
  // through here: closing while the merge request is in flight would
  // swallow its outcome - a failure would render into a dismissed
  // dialog and a success would navigate the agent from wherever they
  // wandered next.
  function safeClose() {
    if (!mergeTickets.isPending) onClose();
  }

  async function confirm() {
    if (selected.length === 0) return;
    setError(null);
    try {
      if (direction === "into") {
        const master = selected[0];
        await mergeTickets.mutateAsync({
          targetTicketNumber: master.ticket_number,
          sourceTicketNumbers: [ticket.ticket_number],
        });
        onMerged(master.public_code, "into", [ticket.public_code]);
      } else {
        await mergeTickets.mutateAsync({
          targetTicketNumber: ticket.ticket_number,
          sourceTicketNumbers: selected.map((s) => s.ticket_number),
        });
        onMerged(
          ticket.public_code,
          "master",
          selected.map((s) => s.public_code),
        );
      }
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  const confirmLabel =
    direction === "into"
      ? selected.length === 1
        ? `Merge [#${ticket.public_code}] into [#${selected[0].public_code}]`
        : "Merge"
      : selected.length > 0
        ? `Merge ${selected.length} ticket${selected.length === 1 ? "" : "s"} into [#${ticket.public_code}]`
        : "Merge";

  return (
    <Modal isOpen={isOpen} onClose={safeClose} title="Merge tickets" size="lg">
      <p className="text-sm text-neutral-700">
        Merging folds a duplicate's conversation, files, and audience into
        the master ticket. Afterwards, replies addressed to the merged
        ticket - by email subject, thread, or link - land on the master.
      </p>

      {/* Direction toggle */}
      <div
        role="radiogroup"
        aria-label="Merge direction"
        className="mt-4 space-y-2"
      >
        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-neutral-200 p-3 text-sm has-[:checked]:border-primary-400 has-[:checked]:bg-primary-50/40">
          <input
            type="radio"
            name="merge-direction"
            className="mt-0.5"
            checked={direction === "into"}
            onChange={() => switchDirection("into")}
            data-testid="merge-direction-into"
          />
          <span>
            <span className="font-medium text-neutral-900">
              Merge [#{ticket.public_code}] into another ticket
            </span>
            <span className="block text-neutral-600">
              Pick the master below. This ticket is folded into it.
            </span>
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-neutral-200 p-3 text-sm has-[:checked]:border-primary-400 has-[:checked]:bg-primary-50/40">
          <input
            type="radio"
            name="merge-direction"
            className="mt-0.5"
            checked={direction === "master"}
            onChange={() => switchDirection("master")}
            data-testid="merge-direction-master"
          />
          <span>
            <span className="font-medium text-neutral-900">
              Make [#{ticket.public_code}] the master
            </span>
            <span className="block text-neutral-600">
              Pick one or more tickets below to fold into this one.
            </span>
          </span>
        </label>
      </div>

      {/* Search */}
      <div className="mt-4">
        <label
          htmlFor="merge-search"
          className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500"
        >
          {direction === "into"
            ? "Find the master ticket"
            : "Find tickets to merge in"}
        </label>
        <input
          id="merge-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by code, subject, or requester"
          autoComplete="off"
          className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {/* Selected chips (visible in both directions; essential for master mode) */}
      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((s) => (
            <button
              key={s.ticket_id}
              type="button"
              onClick={() => toggle(s)}
              className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 hover:bg-primary-200"
              title="Remove from selection"
            >
              [#{s.public_code}]
              <i className="fa-duotone fa-thin fa-xmark" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      {/* Results */}
      <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-neutral-200">
        {debouncedQuery.trim().length === 0 ? (
          <p className="p-3 text-sm text-neutral-500">
            Type to search tickets.
          </p>
        ) : search.isLoading ? (
          <div className="flex items-center gap-2 p-3 text-sm text-neutral-500">
            <Spinner size="sm" /> Searching...
          </div>
        ) : results.length === 0 ? (
          <p className="p-3 text-sm text-neutral-500">No tickets matched.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {results.map((row) => {
              const isSelf = row.ticket_number === ticket.ticket_number;
              const isGemini = isGeminiCode(row.public_code);
              const disabled = isSelf || isGemini;
              const isSelected = selected.some(
                (s) => s.ticket_id === row.ticket_id,
              );
              return (
                <li key={row.ticket_id}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => toggle(row)}
                    aria-pressed={isSelected}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${
                      disabled
                        ? "cursor-not-allowed opacity-45"
                        : isSelected
                          ? "bg-primary-50"
                          : "hover:bg-neutral-50"
                    }`}
                  >
                    <i
                      className={`fa-duotone fa-thin ${
                        isSelected ? "fa-square-check" : "fa-square"
                      } text-neutral-500`}
                      aria-hidden="true"
                    />
                    <span className="w-20 shrink-0 font-mono text-xs text-neutral-600">
                      [#{row.public_code}]
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-neutral-900">
                        {row.subject}
                      </span>
                      <span className="block truncate text-xs text-neutral-500">
                        {row.requester_name || row.requester_email}
                        {isSelf && " - this ticket"}
                        {isGemini && " - Gemini archive, cannot merge"}
                      </span>
                    </span>
                    <StatusBadge status={row.status as TicketStatus} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {requesterDiffers && (
        <Alert variant="warning" className="mt-3">
          The selected ticket{selected.length === 1 ? " has" : "s have"} a
          different requester than [#{ticket.public_code}]. The folded
          ticket's requester is added as a CC watcher on the master so they
          keep receiving replies.
        </Alert>
      )}

      {error && (
        <Alert variant="error" className="mt-3">
          {error}
        </Alert>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={safeClose}
          disabled={mergeTickets.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            void confirm();
          }}
          disabled={selected.length === 0 || mergeTickets.isPending}
          isLoading={mergeTickets.isPending}
          data-testid="merge-confirm"
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

export default MergeTicketModal;
